import moment from 'moment';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Box } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks/useLocalize';
import {
	CircleRemoveIcon,
	DeleteIcon,
	GridIcon,
	MoreAlternateIcon,
	PencilIcon,
} from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { ContextMenu, ContextMenuContext } from '@treams/treams-ui/common/ContextMenu';
import { Popover } from '@treams/treams-ui/common/Popover';
import { Select } from '@treams/treams-ui/common/Select';
import { IconButton } from '@treams/treams-ui/common/button/IconButton';
import { tokens } from '@treams/treams-ui/theme/tokens';

import ArchiveTalentReviewSnapshotModal from '../ArchiveTalentReviewSnapshotModal';
import EditTalentReviewSnapshotModal from '../EditTalentReviewSnapshotModal';
import { type TalentReviewSnapshotContext } from '../TalentReview';
import useTalentReviewPath from '../hooks/useTalentReviewPath';
import { liveGridSwitchOptions, submittedGridSwitch } from './talentReviewGridSwitch';

/** Time to cross the gap between the row and the flyout without it closing. */
const FLYOUT_CLOSE_DELAY_MS = 150;

export default function TalentReviewGridActionsMenu(props: {
	clearDisabled: boolean;
	onClearGrid: () => void;
	snapshot?: TalentReviewSnapshotContext;
	groupID: string;
	templateID: string;
	templateName: string;
	/** Grid day currently open. Switching templates keeps this day. */
	localDate: graphql.Scalars['LocalDate']['input'];
}) {
	const history = useHistory();
	const path = useTalentReviewPath();
	const [modal, setModal] = useState<'edit' | 'archive'>();
	const [templatesOpen, setTemplatesOpen] = useState(false);

	return (
		<>
			<ContextMenu.Target
				menu={
					<ActionsMenu
						{...props}
						templatesOpen={templatesOpen}
						onTemplatesOpen={() => setTemplatesOpen(true)}
						onTemplatesClose={() => setTemplatesOpen(false)}
						onEdit={() => setModal('edit')}
						onArchive={() => setModal('archive')}
					/>
				}
			>
				<IconButton
					type="secondary"
					icon={<MoreAlternateIcon />}
					onClick={() => setTemplatesOpen(false)}
				/>
			</ContextMenu.Target>
			{props.snapshot && modal === 'edit' && (
				<EditTalentReviewSnapshotModal
					snapshot={props.snapshot}
					onClose={() => setModal(undefined)}
				/>
			)}
			{props.snapshot && modal === 'archive' && (
				<ArchiveTalentReviewSnapshotModal
					snapshotID={props.snapshot.id}
					onClose={() => setModal(undefined)}
					onArchived={() =>
						history.push(
							path({
								groupID: props.groupID,
								templateID: props.templateID,
								localDate: moment().format('YYYY-MM-DD'),
							}),
						)
					}
				/>
			)}
		</>
	);
}

function ActionsMenu(props: {
	clearDisabled: boolean;
	onClearGrid: () => void;
	snapshot?: TalentReviewSnapshotContext;
	groupID: string;
	templateID: string;
	templateName: string;
	localDate: graphql.Scalars['LocalDate']['input'];
	templatesOpen: boolean;
	onTemplatesOpen: () => void;
	onTemplatesClose: () => void;
	onEdit: () => void;
	onArchive: () => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const history = useHistory();
	const location = useLocation();
	const path = useTalentReviewPath();
	const menu = useContext(ContextMenuContext);
	const gridLabel = t('talentReviews.v2.menu.grid', { name: props.templateName });

	const templatesQuery = graphql.useTalentReviewTemplatesQuery({
		variables: { isArchived: false, first: 1000 },
		skip: !!props.snapshot,
	});

	const snapshotsQuery = graphql.useTalentReviewSnapshotsQuery({
		variables: { id: props.groupID, isArchived: false },
		skip: !props.snapshot,
	});

	const liveOptions = useMemo(() => {
		if (props.snapshot) {
			return [];
		}

		const templates =
			templatesQuery.data?.talentReviewTemplates.edges.map(({ node }) => ({
				id: node.id,
				title: localize(node.title).text ?? '',
			})) ?? [];

		return liveGridSwitchOptions({ id: props.templateID, title: props.templateName }, templates);
	}, [
		localize,
		props.snapshot,
		props.templateID,
		props.templateName,
		templatesQuery.data?.talentReviewTemplates.edges,
	]);

	const submitted = useMemo(() => {
		if (!props.snapshot) {
			return undefined;
		}

		const submissions =
			snapshotsQuery.data?.group.talentReviewSnapshots.edges.map(({ node }) => ({
				id: node.id,
				localDate: node.localDate,
				createdAt: node.createdAt,
				templateID: node.template.id,
				title: localize(node.template.title).text ?? '',
			})) ?? [];

		return submittedGridSwitch(
			{
				templateID: props.templateID,
				title: props.templateName,
				snapshotID: props.snapshot.id,
			},
			props.localDate,
			submissions,
		);
	}, [
		localize,
		props.localDate,
		props.snapshot,
		props.templateID,
		props.templateName,
		snapshotsQuery.data?.group.talentReviewSnapshots.edges,
	]);

	const options = props.snapshot ? (submitted?.options ?? []) : liveOptions;
	const canSwitch = options.length > 1;

	const selectTemplate = (value?: string | number) => {
		const templateID = value == null ? undefined : String(value);
		if (!templateID || templateID === props.templateID) {
			menu.dismiss();
			return;
		}

		if (props.snapshot) {
			const snapshotID = submitted?.snapshotIDByTemplateID.get(templateID);
			if (snapshotID) {
				history.push(path({ snapshotID, search: location.search, manage: true }));
			}
		} else {
			history.push(
				path({
					groupID: props.groupID,
					templateID,
					localDate: props.localDate,
					search: location.search,
				}),
			);
		}

		menu.dismiss();
	};

	const rowRef = useRef<HTMLDivElement>(null);
	const closeTimer = useRef<number>();
	const [flyoutOrigin, setFlyoutOrigin] = useState<
		{ top: number; left: number; width: number; height: number } | undefined
	>();

	const cancelClose = () => {
		window.clearTimeout(closeTimer.current);
	};

	useEffect(
		() => () => {
			window.clearTimeout(closeTimer.current);
		},
		[],
	);

	const scheduleClose = () => {
		cancelClose();
		closeTimer.current = window.setTimeout(props.onTemplatesClose, FLYOUT_CLOSE_DELAY_MS);
	};

	const showTemplates = () => {
		const row = rowRef.current;
		if (!row) {
			return;
		}

		cancelClose();
		const rowRect = row.getBoundingClientRect();
		let card: HTMLElement = row;
		while (card.parentElement && getComputedStyle(card).position !== 'fixed') {
			card = card.parentElement;
		}
		const cardRect = card.getBoundingClientRect();
		// Negative so the flyout overlaps the parent card instead of leaving a gap.
		const offset = -tokens.radius.xs;

		// Popover sits its offset (5) below origin.top. Select.Menu pads its list
		// by padding.compact, so shift the card up by that and the first template
		// lines up with this row. A negative width makes the popover fall back to
		// the left of the card when the right side does not fit.
		setFlyoutOrigin({
			top: rowRect.top - 5 - tokens.padding.compact,
			left: cardRect.right + offset,
			width: cardRect.left - offset - (cardRect.right + offset),
			height: 0,
		});
		props.onTemplatesOpen();
	};

	return (
		<>
			<ContextMenu>
				{props.snapshot ? (
					<>
						<ContextMenu.Item
							icon={<PencilIcon />}
							title={t('talentReviews.v2.edit')}
							onClick={props.onEdit}
						/>
						<ContextMenu.Separator />
						<ContextMenu.Item
							icon={<DeleteIcon />}
							title={t('talentReviews.v2.delete')}
							type="destructive"
							onClick={props.onArchive}
						/>
					</>
				) : (
					<ContextMenu.Item
						icon={<CircleRemoveIcon />}
						title={t('talentReviews.v2.menu.clearGrid')}
						disabled={props.clearDisabled}
						onClick={props.onClearGrid}
					/>
				)}
				<Box
					ref={rowRef}
					onMouseEnter={canSwitch ? showTemplates : undefined}
					onMouseLeave={canSwitch ? scheduleClose : undefined}
				>
					<ContextMenu.Item
						highlighted={props.templatesOpen}
						icon={<GridIcon />}
						title={gridLabel}
						link={canSwitch}
						preventCloseOnClick={canSwitch}
					/>
				</Box>
			</ContextMenu>
			{props.templatesOpen && flyoutOrigin && (
				<Popover passthrough visible origin={flyoutOrigin} onDismiss={props.onTemplatesClose}>
					<Box onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
						<Select.Menu
							search={false}
							options={options}
							selectedValue={props.templateID}
							onChange={selectTemplate}
						/>
					</Box>
				</Popover>
			)}
		</>
	);
}
